/**
 * Common Utility Functions Module
 * Provides reusable utility functions for string manipulation, date formatting,
 * number abbreviation, and data encoding/decoding operations
 */

import {trackError} from "./error_tracker"

/**
 * Retrieve the username from localStorage
 * @returns {string|null} - The username if available, otherwise null
 */
export function getUsername() {
    try {
        const userinfo = localStorage.getItem("login_user_name");
        if (userinfo) {
            return userinfo || "-"; // Safely return username or null
        }
    } catch (error) {
        trackError("Error retrieving username:<br/><br/>Method Name:getUsername<br>", error)
    }
    return null;
}

/**
 * Extract the filename from a URL
 * @param {string} pmURL - The URL to extract the filename from
 * @returns {string} - The filename
 */
export function getFilenameFromURL(pmURL) {
    try {
        return pmURL.substring(pmURL.lastIndexOf("/") + 1);
    } catch (error) {
        // trackError("Error extracting filename from URL:<br/><br/>Method Name:getFilenameFromURL<br>", error)
        return "";
    }
}

/**
 * Format a date string based on the specified type
 * @param {string} pmDate - The date string
 * @param {string} pmType - Type for formatting (currently unused)
 * @returns {string} - Formatted date string
 */
export function getDateFormat(pmDate, pmType) {
    try {
        const date = getDate(pmDate);
        return (
            ((date.getMonth() > 8) ? (date.getMonth() + 1) : ('0' + (date.getMonth() + 1))) +
            '/' +
            ((date.getDate() > 9) ? date.getDate() : ('0' + date.getDate())) +
            '/' +
            date.getFullYear() +
            " " +
            date.getHours() +
            ":" +
            date.getMinutes() +
            ":" +
            date.getSeconds()
        );
    } catch (error) {
        trackError("Error formatting date:<br/><br/>Method Name:getDateFormat<br>", error)
        return "";
    }
}

/**
 * Create a Date object from a string, handling Safari-specific formats
 * @param {string} timeStr - The date string
 * @returns {Date} - The Date object
 */
export function getDate(timeStr) {
    try {
        let dateObj;
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        if (isSafari) {
            dateObj = new Date(timeStr.replace(/\s/, 'T')); // Handle Safari date format
        } else {
            dateObj = new Date(timeStr);
        }
        return dateObj;
    } catch (error) {
        trackError("Error creating Date object::<br/><br/>Method Name:getDate<br>", error)
        return new Date(); // Return current date as fallback
    }
}

/**
 * Abbreviate a number with suffixes (k, m, b, t)
 * @param {number} number - The number to abbreviate
 * @param {number} decPlaces - Number of decimal places
 * @returns {string} - Abbreviated number
 */
export function abbrNum(number, decPlaces) {
    try {
        decPlaces = Math.pow(10, decPlaces); // Decimal places multiplier
        const abbrev = ["k", "m", "b", "t"]; // Abbreviation array

        for (let i = abbrev.length - 1; i >= 0; i--) {
            const size = Math.pow(10, (i + 1) * 3);
            if (size <= number) {
                number = Math.round(number * decPlaces / size) / decPlaces;
                if (number === 1000 && i < abbrev.length - 1) {
                    number = 1;
                    i++;
                }
                return number + abbrev[i];
            }
        }
        return number.toString();
    } catch (error) {
        trackError("Error abbreviating number::<br/><br/>Method Name:abbrNum<br>", error)
        return number.toString();
    }
}

/**
 * Capitalize the first letter of a string
 * @param {string} str - The string to capitalize
 * @returns {string} - String with the first letter capitalized
 */
export function firstLetterCaps(str) {
    try {
        return str.charAt(0).toUpperCase() + str.slice(1);
    } catch (error) {
        trackError("Error capitalizing string:<br/><br/>Method Name:firstLetterCaps<br>", error)
        return str;
    }
}

/**
 * Truncate a string to a specified length and add ellipsis if necessary
 * @param {string} text - The text to truncate
 * @param {number} maxLength - Maximum allowed length
 * @returns {string} - Truncated string
 */
export function cutString(text, maxLength) {
    try {
        if (typeof text !== "string") {
            throw new Error("The first argument must be a string.");
        }
        if (typeof maxLength !== "number" || maxLength <= 0) {
            throw new Error("The maxLength must be a positive number.");
        }
        return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
    } catch (error) {
        trackError("Error truncating string:<br/><br/>Method Name:cutString<br>", error)
        return text;
    }
}

/**
 * Generic loader placeholder component
 * Currently returns 0 as a stub; commented code shows original JSX implementation
 * @returns {number} Returns 0
 */
export function loader() {
    return 0
    // return (
    //   <div className="d-flex justify-content-center align-items-center" style={{ height: "30vh" }}>
    //     <div className="spinner-border text-primary" role="status">
    //       <span className="visually-hidden">Loading...</span>
    //     </div>
    //   </div>
    // );
  }
  


  
  /**
 * Process loader placeholder component
 * Displays a loading spinner with optional status message
 * Currently returns 1 as a stub; commented code shows original JSX implementation
 * @param {string} pmMessage - Optional message to display with the loader
 * @returns {number} Returns 1
 */
export function processLoader(pmMessage = "") {
    return 1
    // return (
    //   <div className="d-flex justify-content-center align-items-center" id="process-loader">
    //     <div className="spinner-border text-primary process-loader-size" role="status">&nbsp;</div>
    //     <span className="process-loader-message">{pmMessage}</span>
    //   </div>
    // );
  }
  

/**
 * Placeholder for encrypting data (to be implemented)
 */
export function encryptData() {
    console.warn("encryptData function is not implemented yet.");
}

/**
 * Capitalize the first letter of each word in a string
 * Converts string to lowercase first for consistency, then capitalizes each word
 * @param {string} str - The input string to capitalize
 * @returns {string} - String with first letter of each word capitalized
 */
export function capitalizeFirstLetterOfEachWord(str) {
    return str
      .toLowerCase() // Convert the whole string to lowercase for consistency
      .split(' ')    // Split the string into an array of words
      .map(word => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize first letter of each word
      .join(' ');    // Join the words back into a string
}

/**
 * Replace all spaces in a string with underscores
 * Used for converting text to URL-safe or identifier-safe format
 * @param {string} str - The input string
 * @returns {string} - String with spaces replaced by underscores
 */
export function replaceSpacesWithUnderscores(str) {
    return str.replace(/ /g, '_');
}
  

/**
 * Format an ISO 8601 datetime string to YYYY-MM-DD HH:mm:ss format
 * Parses datetime with optional milliseconds and returns standardized format
 * @param {string} inputDate - ISO 8601 formatted datetime string (e.g., "2024-01-25T14:30:45.123Z")
 * @returns {string} - Formatted datetime string in YYYY-MM-DD HH:mm:ss format
 */
export function formatDate(inputDate) {
    // Split date and time
    const [datePart, timePart] = inputDate.split('T');
  
    // Format the date (YYYY-MM-DD)
    const [year, month, day] = datePart.split('-');
  
    // Format the time (hh:mm:ss) and ignore the milliseconds if present
    const [hours, minutes, secondsWithMs] = timePart.split(':');
    const seconds = secondsWithMs.split('.')[0]; // Extract only the seconds part
  
    // Combine and return in the desired format YYYY-MM-DD hh:mm:ss
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}
  

/**
 * Encode special characters in URL filenames and reconstruct the full URL
 * Converts '#' characters to '%23' to ensure proper URL handling
 * @param {string} url - The full URL with filename
 * @returns {string} - URL with encoded filename
 */
export function encodeAndConstructFilename(url){
        // Parse the URL and extract the filename
        const urlParts = url.split('/');
        const filename = urlParts.pop(); // Extract the last part of the URL (filename)
        
        // Encode '#' to '%23' in the filename
        const encodedFilename = filename.replace(/#/g, '%23');
        
        // Reconstruct the full URL
        const updatedUrl = [...urlParts, encodedFilename].join('/');
        return updatedUrl;
};



/**
 * Unescape HTML entities in a string
 * Converts HTML entity &#39; (apostrophe) back to single quote character
 * Note: Only handles single quote entities; other entities are commented out
 * @param {string} str - The HTML-encoded string
 * @returns {string} - String with HTML entities decoded
 */
export function unescapeHTML(str) {
    //return str.replace(/&(amp|lt|gt|quot|#39);/g, function(match) {
        return str.replace(/&(#39);/g, function(match) {
        const htmlEntities = {
            //'&amp;': '&',
            //'&lt;': '<',
            //'&gt;': '>',
            //'&quot;': '"',
            '&#39;': "'"
        };
        return htmlEntities[match];
    });
}

/**
 * Escape HTML entities in a string
 * Converts single quote character to HTML entity &#39;
 * Note: Only handles single quote entities; other entities are commented out
 * @param {string} str - The plain text string
 * @returns {string} - String with HTML entities encoded
 */
export function escapeHTML(str) {
    //return str.replace(/[&<>"']/g, function(match) {
        str = String(str)
        return str.replace(/'/g, function(match) {
        const htmlEntities = {
            // '&': '&amp;',
            // '<': '&lt;',
            // '>': '&gt;',
            // '"': '&quot;',
            "'": '&#39;'
        };
        return htmlEntities[match];
    });
}

